import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Store, Columns3, Layers, MapPin, ArrowRight, Info, ChevronDown, ChevronRight } from 'lucide-react';
import { trackPageView } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const t = {
  tag: 'STRENGTH SCANNER',
  title: 'Compare Karo.',
  titleAccent: 'Smart Choose Karo.',
  subtitle: 'Dekhiye Lion Cement market ke alternatives se kaise compare karta hai aapke project ke liye.',
  projectType: 'Project ka Type',
  location: 'Location',
  selectDistrict: 'District Chuniye',
  budget: 'Bag ka Budget',
  compare: 'Compare Karo',
  params: { strength: 'Mazbooti', setting: 'Setting Speed', purity: 'Material Purity', weather: 'Weather Fit', value: 'Paisa Vasool' },
  tapReveal: 'Jaankaari ke liye tap karein',
  paramDesc: {
    strength: 'Cement kitna mazboot hai — load aur stress sehne ki kshamta.',
    setting: 'Cement kitni jaldi set hota hai aur agle kaam ke liye tayyar hota hai.',
    purity: 'Raw material ki quality — kam impurities matlab behtar result.',
    weather: 'Cement garmi, baarish ya humidity mein kitna tikta hai.',
    value: 'Quality aur price dono milaakar kitna fayda hai.',
  },
  lion: 'Lion Cement',
  premium: 'Premium Cement',
  popular: 'Popular Cement',
  economy: 'Economy Cement',
  checkSavings: 'Bachat Dekhein',
  requestSample: 'Free Sample Mangayein',
  disclaimer: 'Result anumaan hain aur usage, conditions aur location ke anusar alag ho sakte hain.',
};

const projectTypes = [
  { id: 'house', icon: Home, label: 'Ghar' },
  { id: 'shop', icon: Store, label: 'Dukaan' },
  { id: 'wall', icon: Columns3, label: 'Compound Wall' },
  { id: 'slab', icon: Layers, label: 'Slab' },
];

const params = ['strength', 'setting', 'purity', 'weather', 'value'];
const brands = ['lion', 'premium', 'popular', 'economy'];
const brandColors = { lion: '#E85A1C', premium: '#6B7280', popular: '#4B5563', economy: '#374151' };

const CementBagFill = ({ score, color, delay = 0 }) => (
  <div className="relative w-full h-24 bg-void/50 rounded-lg overflow-hidden border border-white/5">
    <motion.div
      className="absolute bottom-0 left-0 right-0 rounded-b-lg"
      style={{ backgroundColor: color }}
      initial={{ height: 0 }}
      animate={{ height: `${score}%` }}
      transition={{ duration: 1.2, delay, ease: 'easeOut' }}
    />
    <div className="absolute inset-0 flex items-center justify-center">
      <motion.span
        className="text-white text-sm font-display font-bold drop-shadow-lg"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: delay + 0.8 }}
      >
        {score}%
      </motion.span>
    </div>
  </div>
);

const StrengthScannerPage = () => {
  const [projectType, setProjectType] = useState('house');
  const [districts, setDistricts] = useState({});
  const [location, setLocation] = useState('');
  const [budget, setBudget] = useState(400);
  const [results, setResults] = useState(null);
  const [expandedParam, setExpandedParam] = useState(null);
  const [loading, setLoading] = useState(false);
  const resultsRef = useRef(null);

  useEffect(() => {
    trackPageView('/strength-scanner', 'Strength Scanner');
    fetch(`${BACKEND_URL}/api/districts`).then(r => r.json()).then(setDistricts);
  }, []);

  const handleCompare = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strength-scanner/compare`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_type: projectType, location, budget }),
      });
      const data = await res.json();
      setResults(data);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-void" data-testid="strength-scanner-page">
      <SEOHead
        title="Strength Scanner"
        description="Compare Lion Cement quality against competitor brands. Interactive tool analyzing strength, setting time, purity, weather resistance, and value."
        path="/strength-scanner"
      />
      {/* Hero */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <motion.div className="absolute top-1/3 right-0 w-[600px] h-[600px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(232,90,28,0.1) 0%, transparent 60%)' }} />
        <div className="container mx-auto px-4 md:px-8 max-w-6xl relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            {/* Step indicator */}
            <div className="flex items-center gap-3 mb-4">
              <Link to="/tools" className="text-gray-500 hover:text-white text-xs font-mono transition-colors">Smart Tools</Link>
              <span className="text-gray-700">/</span>
              <span className="text-coral text-xs font-mono">Step 1</span>
              <span className="text-gray-700">/</span>
              <Link to="/savings-calculator" className="text-gray-500 hover:text-mint text-xs font-mono transition-colors">Step 2</Link>
              <span className="text-gray-700">/</span>
              <Link to="/calculator" className="text-gray-500 hover:text-blue-400 text-xs font-mono transition-colors">Step 3</Link>
            </div>
            <span className="text-coral font-mono text-sm mb-4 block">{t.tag}</span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white">
              {t.title}<br /><span className="text-gradient">{t.titleAccent}</span>
            </h1>
            <p className="text-gray-400 text-base md:text-lg mt-4 max-w-xl">{t.subtitle}</p>
          </motion.div>
        </div>
      </section>

      {/* Inputs */}
      <section className="pb-8">
        <div className="container mx-auto px-4 md:px-8 max-w-6xl">
          <div className="bg-surface border border-white/5 rounded-2xl p-6 md:p-8 space-y-6">
            {/* Project Type */}
            <div>
              <label className="font-display text-sm font-medium text-white mb-4 block">{t.projectType}</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {projectTypes.map(pt => (
                  <button key={pt.id} onClick={() => setProjectType(pt.id)} data-testid={`scanner-type-${pt.id}`}
                    className={`p-4 rounded-xl text-center transition-all ${projectType === pt.id ? 'bg-coral text-white border border-coral' : 'bg-void border border-white/10 text-gray-300 hover:border-white/30'}`}>
                    <pt.icon className="w-6 h-6 mx-auto mb-2" />
                    <span className="text-sm font-display font-medium">{pt.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Location */}
              <div>
                <label className="font-display text-sm font-medium text-white mb-2 block flex items-center gap-2"><MapPin className="w-4 h-4 text-coral" /> {t.location}</label>
                <div className="relative">
                  <select value={location} onChange={(e) => setLocation(e.target.value)} data-testid="scanner-location"
                    className="w-full bg-void border border-white/10 focus:border-coral rounded-xl px-4 py-3.5 text-white outline-none appearance-none text-sm">
                    <option value="">{t.selectDistrict}</option>
                    <optgroup label="Gujarat">{(districts.Gujarat || []).map(d => <option key={d} value={d}>{d}</option>)}</optgroup>
                    <optgroup label="Rajasthan">{(districts.Rajasthan || []).map(d => <option key={d} value={d}>{d}</option>)}</optgroup>
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                </div>
              </div>

              {/* Budget */}
              <div>
                <label className="font-display text-sm font-medium text-white mb-2 block">{t.budget}: <span className="text-coral">Rs.{budget}</span></label>
                <input type="range" min={300} max={500} step={10} value={budget} onChange={e => setBudget(Number(e.target.value))} data-testid="scanner-budget"
                  className="w-full h-2 bg-void rounded-lg appearance-none cursor-pointer accent-coral" />
                <div className="flex justify-between text-xs text-gray-600 mt-1"><span>Rs.300</span><span>Rs.500</span></div>
              </div>
            </div>

            <button onClick={handleCompare} disabled={loading} data-testid="scanner-compare-btn"
              className="w-full flex items-center justify-center gap-2 bg-coral hover:bg-coral-dark text-white font-display font-medium py-4 rounded-full transition-all disabled:opacity-50">
              {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <>{t.compare} <ArrowRight className="w-5 h-5" /></>}
            </button>
          </div>
        </div>
      </section>

      {/* Results */}
      <AnimatePresence>
        {results && (
          <motion.section ref={resultsRef} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="pb-16">
            <div className="container mx-auto px-4 md:px-8 max-w-6xl space-y-8">
              {/* Verdict */}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
                className="bg-gradient-to-r from-coral/10 to-transparent border border-coral/20 rounded-2xl p-6" data-testid="scanner-verdict">
                <p className="text-white font-display text-lg md:text-xl font-medium leading-relaxed">"{results.verdict}"</p>
              </motion.div>

              {/* Comparison Grid */}
              <div className="bg-surface border border-white/5 rounded-2xl overflow-hidden">
                {/* Header */}
                <div className="grid grid-cols-5 gap-0 border-b border-white/5">
                  <div className="p-4 text-gray-500 text-xs font-mono" />
                  {brands.map(b => (
                    <div key={b} className={`p-4 text-center ${b === 'lion' ? 'bg-coral/10 border-b-2 border-coral' : ''}`}>
                      <span className={`text-xs font-display font-semibold ${b === 'lion' ? 'text-coral' : 'text-gray-400'}`}>{t[b]}</span>
                    </div>
                  ))}
                </div>

                {/* Parameters */}
                {params.map((param, pi) => (
                  <div key={param}>
                    <button onClick={() => setExpandedParam(expandedParam === param ? null : param)}
                      className="w-full grid grid-cols-5 gap-0 border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                      <div className="p-4 flex items-center gap-2 text-left">
                        <span className="text-gray-300 text-sm font-display">{t.params[param]}</span>
                        <Info className="w-3 h-3 text-gray-600 flex-shrink-0" />
                      </div>
                      {brands.map((b, bi) => (
                        <div key={b} className={`p-3 ${b === 'lion' ? 'bg-coral/5' : ''}`}>
                          <CementBagFill score={results.comparison[b][param]} color={brandColors[b]} delay={pi * 0.1 + bi * 0.05} />
                        </div>
                      ))}
                    </button>
                    <AnimatePresence>
                      {expandedParam === param && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden bg-void/50 border-b border-white/5">
                          <p className="p-4 text-gray-400 text-xs">{t.paramDesc[param]}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/savings-calculator" data-testid="scanner-cta-savings"
                  className="flex-1 flex items-center justify-center gap-2 bg-coral hover:bg-coral-dark text-white font-display font-medium py-4 rounded-full transition-all">
                  {t.checkSavings} <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/contact" data-testid="scanner-cta-sample"
                  className="flex-1 flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 text-white font-display font-medium py-4 rounded-full transition-all">
                  {t.requestSample}
                </Link>
              </div>

              {/* Next steps flow */}
              <div className="bg-surface-light border border-white/5 rounded-2xl p-5 flex items-center justify-between" data-testid="scanner-flow-next">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-mint/10 flex items-center justify-center text-mint font-display font-bold text-xs">2</div>
                  <div>
                    <p className="text-white text-sm font-display font-medium">Next: Savings Calculator</p>
                    <p className="text-gray-500 text-xs">Dekhein kitna bachate hain</p>
                  </div>
                </div>
                <Link to="/savings-calculator" className="text-mint text-sm font-display flex items-center gap-1 hover:gap-2 transition-all">Jao <ChevronRight className="w-4 h-4" /></Link>
              </div>

              <p className="text-gray-600 text-xs text-center">{t.disclaimer}</p>
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StrengthScannerPage;
